import { yupResolver } from "@hookform/resolvers/yup";
import {
  useGetCompanySettingQuery,
  useUpdateCompanySettingMutation,
} from "AppQuries/setting/company-settings";
import CompanyOpenCloseForm from "Components/Form/CompanyOpenCloseForm";
import Input from "Components/UI/Input";
import LoadingSpinner from "Components/UI/LoadingSpinner";
import { companySettingsValidation } from "core/Operations/ValidationSchema/settingsValidationSchema";
import { loggedInUser } from "core/redux/slice/authSlice";
import Error404 from "MainPages/ErrorPages/error404";
import React, { useEffect, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Save } from "react-feather";
import { FormProvider, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import { message } from "antd";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import OpeningAndClosingTime from "Components/UI/CompanySettings/OpeingAndClosingTime";
import { getCurrentFormattedDate } from "core/Operations/currentDateTime";
import CompanyInfo from "./CompanySetting/CompanyInfo";
import EmailConfig from "./CompanySetting/EmailConfig";
import HeaderDetails from "./CompanySetting/HeaderDetails";
import Can from "core/Operations/Can";
import { accessCodes } from "core/json/accessCode";

const CompanySetting = () => {
  const userData = useSelector(loggedInUser);
  const methods = useForm({
    defaultValues: {
      modifyUser: userData.userName,
      modifyDate: getCurrentFormattedDate(),
      emailConfigurations: [
        {
          emailConfigID: 0,
          companyCode: userData.companyCode,
          smtpServer: "",
          smtpPort: 0,
          senderEmail: "",
          senderPassword: "",
          useSSL: false,
          useTLS: false,
          isEnabled: false,
          createUser: userData.userName,
          createdDate: getCurrentFormattedDate(),
          updateUser: userData.userName,
          lastUpdated: getCurrentFormattedDate(),
        },
      ],
    },
    resolver: yupResolver(companySettingsValidation),
  });

  const {
    data: companySettings,
    isFetching,
    isFetched,
    isError,
    error,
  } = useGetCompanySettingQuery(
    {
      companyCode: userData?.companyCode,
    },
    {
      enabled: !!userData?.companyCode,
    }
  );

  const {
    mutate: updateCompanySetting,
    isPending,
    isError: isUpdateError,
    error: updateError,
  } = useUpdateCompanySettingMutation();

  const {
    register,
    watch,
    setValue,
    handleSubmit,
    trigger,
    formState: { errors },
  } = methods;

  const onSubmit = (data) => {
    const requestData = {
      ...data,
      userMaster: [],
      modifyUser: userData.userName,
      modifyDate: getCurrentFormattedDate(),
      isActive: data.companySettings[0].isActive,
    };

    updateCompanySetting(requestData, {
      onSuccess: (data) => {
        if (data[0].response === true) {
          Swal.fire({
            type: "success",
            title: "Updated!",
            text: "Company Settings Updated!",
            confirmButtonClass: "btn btn-success",
          });
        } else {
          Swal.fire({
            title: "Failed",
            text: data[0].errorMessage,
            type: "warning",
            confirmButtonColor: "#3085d6",
            confirmButtonText: "Ok",
            confirmButtonClass: "btn btn-primary",
          });
        }
      },
      onError: (error) => {
        console.log(error);
        message(error.message || "Something went wrong.");
      },
    });
  };

  const [loadingData, setLoadingData] = useState(true);
  const [descriptions, setDescriptions] = useState([]);

  useEffect(() => {
    if (companySettings) {
      Object.keys(companySettings).forEach((key) => {
        setValue(key, companySettings[key]);
      });
      setDescriptions(companySettings?.headerDescription || []);
    }
    setLoadingData(false);
  }, [companySettings]);

  if (isFetching || isPending || loadingData) return <LoadingSpinner />;
  if (isError) return <Error404 error={error} />;
  if (isUpdateError) return <Error404 error={updateError} />;
  return (
    <div className="page-wrapper">
      <FormProvider {...methods}>
        <div className="content">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="page-header">
              <div className="add-item d-flex">
                <div className="page-title">
                  <h4>Company Settings</h4>
                  <h6>Manage your company settings</h6>
                </div>
              </div>
              <div className="page-btn">
                <Can
                  accessModule={accessCodes.settings}
                  accessCode={accessCodes.company_setting}
                  accessType="haveAdd"
                >
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="add-new-tooltip">Save Settings</Tooltip>
                    }
                  >
                    <button type="submit" className="btn btn-added">
                      <Save className="me-2" />
                      Save Settings
                    </button>
                  </OverlayTrigger>
                </Can>
              </div>
            </div>

            <div className="row">
              <div className="card border-0">
                <div className="card-header">
                  {/* <ul className="nav nav-tabs nav-tabs-bottom nav-justified"> */}
                  <ul className="nav nav-tabs nav-tabs-solid nav-justified mb-3">
                    <li className="nav-item">
                      <Link
                        className="nav-link active"
                        to="#company-info"
                        data-bs-toggle="tab"
                      >
                        Company Info
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link
                        className="nav-link"
                        to="#email-config"
                        data-bs-toggle="tab"
                      >
                        Email Config
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link
                        className="nav-link"
                        to="#header-details"
                        data-bs-toggle="tab"
                      >
                        Header Details
                      </Link>
                    </li>
                    {/* <li className="nav-item">
                      <Link
                        className="nav-link"
                        to="#opening-closing-time"
                        data-bs-toggle="tab"
                      >
                        Opening & Closing Time
                      </Link>
                    </li> */}
                  </ul>
                </div>
                <div className="card-body">
                  {!loadingData && watch("companySettings") && (
                    <div className="tab-content ">
                      <div className="tab-pane show active" id="company-info">
                        <CompanyInfo descriptions={descriptions} />
                      </div>
                      <div className="tab-pane" id="email-config">
                        <EmailConfig />
                      </div>
                      <div className="tab-pane" id="header-details">
                        <HeaderDetails descriptions={descriptions} />
                      </div>

                      {/* <div className="tab-pane" id="opening-closing-time">
                        <OpeningAndClosingTime />
                      </div> */}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>
        </div>
      </FormProvider>
    </div>
  );
};

export default CompanySetting;
